// primitives.jsx — the small vocabulary every screen is built from.
// Styling lives in index.css; these components exist to keep markup honest
// (one <Pill>, not fourteen hand-rolled spans) and to hold the few behaviours
// that are easy to get wrong on a phone — sheet scroll locking, escape keys,
// destructive confirmation.

import React from 'react';

/* ── icons (inline, 20px grid — no icon font to load) ─────────────── */

const paths = {
  overview: 'M4 13h6V4H4v9Zm0 7h6v-5H4v5Zm9 0h7v-9h-7v9Zm0-16v5h7V4h-7Z',
  orgs: 'M3 21V9l6-4 6 4v12H3Zm12 0h6V13l-6-3M6 12h3m-3 4h3',
  users: 'M16 19v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1M9.5 10a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm11 9v-1a4 4 0 0 0-3-3.9M16 3.6a4 4 0 0 1 0 7.7',
  monitor: 'M3 15l4-6 4 4 3-6 4 8M3 20h18M3 4v16',
  // a carton: a box with its lid's fold line
  cartons: 'M3 7.5 12 3l9 4.5v9L12 21l-9-4.5v-9Zm0 0L12 12l9-4.5M12 12v9',
  support: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-5v.5m0-3.2c0-1.6 2-1.8 2-3.4a2 2 0 1 0-4 0',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Zm5.5-1.5L21 21',
  chev: 'M9 6l6 6-6 6',
  back: 'M15 6l-6 6 6 6',
  close: 'M6 6l12 12M18 6L6 18',
  refresh: 'M20 12a8 8 0 1 1-2.4-5.7M20 4v4h-4',
  plus: 'M12 5v14M5 12h14',
  check: 'M4 12.5 9 17.5 20 6.5',
  // Feather "settings" (MIT), its circle written as a path: Icon draws one `d`.
  gear: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z'
    + 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 '
    + '1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06'
    + 'a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 '
    + '2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0'
    + 'l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 '
    + '1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9'
    + 'a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1Z',
  alert: 'M12 8v5m0 3v.5M10.3 3.9 2.6 17.3A2 2 0 0 0 4.3 20.3h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
};

export function Icon({ name, size = 20, ...rest }) {
  const d = paths[name];
  if (!d) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden {...rest}>
      <path d={d} />
    </svg>
  );
}

/* ── atoms ────────────────────────────────────────────────────────── */

export function Pill({ tone = '', children, ...rest }) {
  return <span className={`pill ${tone}`} {...rest}>{children}</span>;
}

export function Button({ variant = '', small, block, children, loading, ...rest }) {
  return (
    <button type="button"
            className={`btn ${variant} ${small ? 'small' : ''} ${block ? 'block' : ''}`}
            disabled={rest.disabled || loading} {...rest}>
      {loading && <span className="spinner" />}
      {children}
    </button>
  );
}

export function Field({ label, children, hint }) {
  return (
    <div className="field">
      {label && <label>{label}</label>}
      {children}
      {hint && <div className="dim" style={{ fontSize: 11 }}>{hint}</div>}
    </div>
  );
}

export function Search({ value, onChange, placeholder = 'Search', ...rest }) {
  return (
    <div className="search">
      <Icon name="search" size={17} />
      <input className="input" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)}
             type="search" autoCapitalize="none" autoCorrect="off"
             spellCheck={false} {...rest} />
    </div>
  );
}

export function Chips({ options, value, onChange }) {
  return (
    <div className="chips" role="group">
      {options.map((o) => (
        <button key={o.value} type="button" className="chip"
                aria-pressed={value === o.value}
                onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Stat({ label, value, foot, alert, onClick }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag className={`stat ${alert ? 'is-alert' : ''}`} onClick={onClick}
         type={onClick ? 'button' : undefined}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {foot && <div className="stat-foot">{foot}</div>}
    </Tag>
  );
}

export function Card({ title, action, children, bodyless }) {
  return (
    <section className="card">
      {(title || action) && (
        <header className="card-head">
          <h2 style={{ flex: 1, minWidth: 0 }}>{title}</h2>
          {action}
        </header>
      )}
      {bodyless ? children : <div className="card-body">{children}</div>}
    </section>
  );
}

export function Note({ tone = '', title, children }) {
  return (
    <div className={`note ${tone}`}>
      {title && <div className="note-title">{title}</div>}
      {children}
    </div>
  );
}

export function Empty({ title, children }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      {children}
    </div>
  );
}

export function Row({ onClick, children, href }) {
  const Tag = onClick || href ? 'button' : 'div';
  return (
    <Tag className="list-row" type={Tag === 'button' ? 'button' : undefined}
         onClick={onClick}>
      {children}
      {(onClick || href) && <Icon name="chev" size={16} className="chev" />}
    </Tag>
  );
}

export function Skeleton({ rows = 3 }) {
  return (
    <div className="stack" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 54 }} />
      ))}
    </div>
  );
}

/** One place that decides what a failed request looks like, so no screen
 *  invents its own. Always offers the retry — a superuser in the field is
 *  often on a network that fails once and works the second time. */
export function ErrorState({ error, onRetry, text }) {
  return (
    <Note tone="danger" title={error?.status ? `Error ${error.status}` : 'Error'}>
      <div style={{ marginBottom: onRetry ? 10 : 0 }}>{text}</div>
      {onRetry && <Button small onClick={onRetry}><Icon name="refresh" size={15} />Try again</Button>}
    </Note>
  );
}

/* ── sheet ────────────────────────────────────────────────────────── */

/**
 * Bottom sheet on a phone, centred dialog from 900px up (index.css does the
 * switch). Locks the body scroll while open — without that, scrolling to the
 * bottom of a sheet on iOS continues into the page behind it and the sheet
 * drifts off screen.
 */
export function Sheet({ title, subtitle, onClose, children, footer }) {
  React.useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="scrim" onClick={onClose} role="presentation">
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title}
           onClick={(e) => e.stopPropagation()}>
        <div className="sheet-grip" />
        <header className="sheet-head">
          <div className="col" style={{ flex: 1 }}>
            <div className="sheet-title truncate">{title}</div>
            {subtitle && <div className="sub truncate">{subtitle}</div>}
          </div>
          <button className="btn ghost icon" onClick={onClose} aria-label="Close">
            <Icon name="close" size={19} />
          </button>
        </header>
        <div className="sheet-body">{children}</div>
        {footer && <div className="sheet-foot">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Confirmation for anything that changes another person's access.
 *
 * Every destructive action in this app happens to someone who is not in the
 * room — deactivating an org signs out a whole plant. `confirmWord` forces the
 * org or user name to be typed for the irreversible-feeling ones, so a mis-tap
 * on a phone in a pocket cannot revoke a shift's worth of sessions.
 */
export function ConfirmSheet({ title, body, confirmLabel = 'Confirm', confirmWord,
                               danger, onConfirm, onClose, busy }) {
  const [typed, setTyped] = React.useState('');
  const ready = !confirmWord || typed.trim() === confirmWord;
  return (
    <Sheet title={title} onClose={onClose}
           footer={(
             <>
               <Button onClick={onClose} disabled={busy}>Cancel</Button>
               <Button variant={danger ? 'danger' : 'primary'} loading={busy}
                       disabled={!ready} onClick={onConfirm}>{confirmLabel}</Button>
             </>
           )}>
      <div className="stack">
        <div style={{ fontSize: 13.5, lineHeight: 1.55 }}>{body}</div>
        {confirmWord && (
          <Field label={`Type “${confirmWord}” to confirm`}>
            <input className="input" value={typed} autoCapitalize="none"
                   onChange={(e) => setTyped(e.target.value)} />
          </Field>
        )}
      </div>
    </Sheet>
  );
}

/* ── page furniture ───────────────────────────────────────────────── */

export function PageHead({ title, sub, action }) {
  return (
    <div className="page-head">
      <div className="col" style={{ flex: 1 }}>
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {action}
    </div>
  );
}

export function RefreshButton({ onClick, loading, at }) {
  return (
    <div className="row" style={{ gap: 8 }}>
      {at && <span className="dim mono" style={{ fontSize: 10 }}>
        {new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })}
      </span>}
      <button className="btn ghost icon" onClick={onClick} aria-label="Refresh"
              disabled={loading}>
        {loading ? <span className="spinner" /> : <Icon name="refresh" size={18} />}
      </button>
    </div>
  );
}

/** Definition list used by every detail view. Values that are missing render
 *  as an em dash rather than disappearing — a blank row is ambiguous. */
export function KV({ items }) {
  return (
    <dl className="kv">
      {items.filter(Boolean).map(([k, v]) => (
        <React.Fragment key={k}>
          <dt>{k}</dt>
          <dd>{v === null || v === undefined || v === '' ? <span className="dim">—</span> : v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}
